import { z } from 'zod';
import TourOrganizerInsurance from '@/features/dashboard/tours/types/TourOrganizerInsurance.ts';

export const destinationSchema = z.object({
  country: z
    .string({ error: 'Kraj jest wymagany' })
    .min(1, 'Kraj jest wymagany'),
  city: z
    .string({ error: 'Miejscowość jest wymagana' })
    .min(1, 'Miejscowość jest wymagana')
    .max(255, 'Miejscowość nie może przekraczać 255 znaków'),
  street: z
    .string()
    .max(255, 'Ulica nie może przekraczać 255 znaków')
    .optional(),
  postalCode: z
    .string()
    .max(32, 'Kod pocztowy nie może przekraczać 32 znaków')
    .optional(),
});

export const tourSchema = z
  .object({
    tourName: z
      .string({ error: 'Nazwa wyjazdu jest wymagana 1' })
      .min(1, 'Nazwa wyjazdu jest wymagana')
      .max(255, 'Nazwa wyjazdu nie może przekraczać 255 znaków'),
    destination: destinationSchema,
    startDate: z
      .date({ error: 'Data rozpoczęcia jest wymagana' })
      .min(new Date(), 'Data rozpoczęcia musi być w przyszłości'),
    startDateTime: z
      .string({ error: 'Godzina rozpoczęcia jest wymagana' })
      .refine((val) => /^([0-1]\d|2[0-3]):([0-5]\d)$/.test(val), {
        message: 'Godzina musi być w formacie HH:mm',
      }),
    endDate: z
      .date({ error: 'Data zakończenia jest wymagana' })
      .min(new Date(), 'Data musi być w przyszłości'),
    endDateTime: z
      .string({ error: 'Godzina jest wymagana' })
      .refine((val) => /^([0-1]\d|2[0-3]):([0-5]\d)$/.test(val), {
        message: 'Godzina musi być w formacie HH:mm',
      }),
    registrationStartDate: z.date().optional(),
    registrationStartDateTime: z
      .string()
      .refine((val) => val === '' || /^([0-1]\d|2[0-3]):([0-5]\d)$/.test(val), {
        message: 'Godzina musi być w formacie HH:mm',
      })
      .nullish(),
    registrationEndDate: z.date().optional(),
    registrationEndDateTime: z
      .string()
      .refine((val) => val === '' || /^([0-1]\d|2[0-3]):([0-5]\d)$/.test(val), {
        message: 'Godzina musi być w formacie HH:mm',
      })
      .nullish(),
    allowRegistrationDuringTour: z.boolean().default(false),
    tourOrganizerInsurance: z.enum(TourOrganizerInsurance).optional(),
    tourChatInviteLink: z.string().optional(),
    hasParticipantLimit: z.boolean().default(false),
    participantLimit: z.number().optional(),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;

      const diffInHours =
        (data.endDate.getTime() - data.startDate.getTime()) / (1000 * 60 * 60);
      return diffInHours >= 2;
    },
    {
      message:
        'Data zakończenia musi być co najmniej 2 godziny później niż data rozpoczęcia',
      path: ['endDate'],
    },
  )
  .refine(
    (data) => {
      if (!data.registrationStartDate || !data.registrationEndDate) return true;

      const diffInHours =
        (data.registrationEndDate.getTime() -
          data.registrationStartDate.getTime()) /
        (1000 * 60 * 60);
      return diffInHours >= 2;
    },
    {
      message:
        'Data zakończenia rejestracji musi być co najmniej 2 godziny później niż data rozpoczęcia rejestracji',
      path: ['registrationEndDate'],
    },
  )
  // Granica okna zapisów — ta sama reguła co `registrationHardLimit()` po stronie
  // backendu: bez flagi zapisy gasną o starcie wyjazdu, z flagą trwają do końca.
  .refine(
    (data) => {
      if (data.allowRegistrationDuringTour) return true;
      if (!data.registrationStartDate || !data.registrationEndDate) return true;
      return new Date(data.registrationEndDate) <= new Date(data.startDate);
    },
    {
      message: 'Rejestracja musi zakończyć się przed rozpoczęciem wyjazdu',
      path: ['registrationEndDate'],
    },
  )
  .refine(
    (data) => {
      if (!data.allowRegistrationDuringTour) return true;
      if (!data.registrationStartDate || !data.registrationEndDate) return true;
      return new Date(data.registrationEndDate) <= new Date(data.endDate);
    },
    {
      message: 'Rejestracja musi zakończyć się przed zakończeniem wyjazdu',
      path: ['registrationEndDate'],
    },
  )
  .refine(
    (data) => {
      if (!data.registrationStartDate) return true;
      return !!data.registrationStartDateTime;
    },
    {
      message: 'Godzina wymagana',
      path: ['registrationStartDateTime'],
    },
  )
  .refine(
    (data) => {
      if (!data.registrationEndDate) return true;
      return !!data.registrationEndDateTime;
    },
    {
      message: 'Godzina wymagana',
      path: ['registrationEndDateTime'],
    },
  )
  .refine(
    (data) => {
      return !(
        data.hasParticipantLimit &&
        (!data.participantLimit || data.participantLimit <= 0)
      );
    },
    {
      message: 'Limit uczestników musi być większy od 0',
      path: ['participantLimit'],
    },
  );

export type TourFormInputData = z.input<typeof tourSchema>;
export type TourFormData = z.infer<typeof tourSchema>;
